import app from 'flarum/forum/app';
import { override, extend } from 'flarum/common/extend';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import TagDiscussionModal from 'flarum/tags/forum/components/TagDiscussionModal';

/**
 * Twilight BBS — Tags Optional
 * =============================
 *
 * 让讨论标签完全可选，并修正标签选择框的显示。
 *
 * ── 需求 ──────────────────────────────────────────────────
 *  1. 发帖时不强制选择标签（不弹窗、不阻止提交）
 *  2. 主动点"选择标签"时，标签列表正常显示
 *  3. 移除 "Bypass tag requirements" 勾选框
 *
 * ── 实现要点 ──────────────────────────────────────────────
 *
 * 【发帖提交】
 * flarum/tags 用 override() 包裹 DiscussionComposer.prototype.onsubmit，
 * 在"未选标签/数量不满足限制"时弹窗阻止提交。由于扩展按 ID 字母序加载
 * （"flarum-tags" 在前），原型上已是 tags 包装后的版本，
 * 因此这里直接复刻 core 的提交逻辑，彻底绕开弹窗判断。
 * 已选标签仍会提交 —— 那由 tags 对 this.data() 的包装负责。
 *
 * 【标签选择框】
 * ⚠️ extend() 的回调签名是 (value, ...args)：
 *    第一个参数是原方法的「返回值」，真正的参数从第二个开始。
 *    initAttrs 是静态方法，返回 undefined，参数为 attrs，
 *    因此必须写成 (value, attrs) —— 写成 (attrs) 会拿到 undefined 并报错。
 *
 * 【为什么不用把 max 设为 0】
 * max_*_tags = 0 会让 TagSelectionModal.getFilteredTags() 判定
 * "已达上限"（selected.length >= max.total），把所有标签过滤掉，
 * 表现为选择框里空空如也。所以保留合理上限（max=1/3），只把 min 设为 0。
 */
app.initializers.add('twilight-tags-optional', () => {
  // ───────────────────────────────────────────────────────────
  // 1) 发帖提交：跳过 flarum/tags 的强制选择标签弹窗
  // ───────────────────────────────────────────────────────────
  override(DiscussionComposer.prototype, 'onsubmit', function () {
    (this as any).loading = true;

    // this.data() 已被 tags 扩展包装，会包含 relationships.tags
    const data = (this as any).data();

    app.store
      .createRecord('discussions')
      .save(data)
      .then((discussion: any) => {
        (this as any).composer.hide();
        app.discussions.refresh();
        m.route.set(app.route.discussion(discussion));
      }, (this as any).loaded.bind(this));
  });

  // ───────────────────────────────────────────────────────────
  // 2) 标签选择框：移除 bypass 勾选框 + 放宽 min 限制
  //    注意参数顺序：(value, attrs)
  // ───────────────────────────────────────────────────────────
  extend(TagDiscussionModal, 'initAttrs', function (_value: unknown, attrs: any) {
    if (!attrs?.limits) return;

    // 勾选框的显示条件就是 limits.allowBypassing，关闭它即可移除勾选框
    attrs.limits.allowBypassing = false;

    // 放宽下限，确保确认按钮不因"未达下限"而禁用
    if (attrs.limits.min) {
      attrs.limits.min.primary = 0;
      attrs.limits.min.secondary = 0;
      attrs.limits.min.total = 0;
    }

    // 不强制重置：允许"清空标签后直接确认"
    attrs.allowResetting = false;
  });
});
